'use client';

import React from 'react';
import { ruCountryFor } from '@/i18n/countryCases';
import { serializeJsonLd } from '@/lib/structuredData';

const SERVICE_LABELS = {
    esim:      { ru: 'eSIM провайдер',          en: 'eSIM provider' },
    vpn:       { ru: 'VPN сервис',               en: 'VPN service' },
    tours:     { ru: 'Туры и экскурсии',         en: 'Tours and excursions' },
    transport: { ru: 'Транспортные услуги',      en: 'Transport services' },
    stay:      { ru: 'Размещение и отели',       en: 'Accommodation and hotels' },
    insurance: { ru: 'Туристическая страховка',  en: 'Travel insurance' },
};

const SERVICE_SHORT = {
    esim: 'eSIM', vpn: 'VPN', tours: 'Tours',
    transport: 'Transport', stay: 'Accommodation', insurance: 'Insurance',
};

/**
 * Компонент для генерации Service структурированных данных для провайдеров
 * Альтернатива LocalBusiness, более подходит для онлайн-сервисов
 */
export function ProviderServiceJsonLd({
    provider,
    country,
    countryName,
    serviceType,
    lang = 'en',
    url
}) {
    if (!provider) return null;

    const providerName = provider.title || provider.name || '';
    const providerUrl = provider.mainPageUrl || provider.trackingBaseUrl || provider.redirectBaseUrl || '';
    const svcLabel = SERVICE_LABELS[serviceType]?.[lang] || serviceType;
    const svcShort = SERVICE_SHORT[serviceType] || serviceType;
    const countryPhrase = lang === 'ru' ? ruCountryFor(countryName) : `for ${countryName}`;
    const providerDescription = provider.description ||
        `${providerName} - ${svcLabel} ${countryPhrase}`;

    const schema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `${providerName} ${svcShort} ${countryPhrase}`,
        "description": providerDescription,
        "provider": {
            "@type": "Organization",
            "name": providerName,
            "url": providerUrl || undefined,
            "logo": provider.logoUrl || undefined
        },
        "serviceType": svcLabel,
        "areaServed": {
            "@type": "Country",
            "name": countryName || country,
            "alternateName": country
        },
        "availableChannel": {
            "@type": "ServiceChannel",
            "serviceUrl": url || providerUrl || undefined
        }
    };

    // Удаляем undefined поля
    const cleanSchema = Object.fromEntries(
        Object.entries(schema).filter(([_, value]) => value !== undefined)
    );

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
                __html: serializeJsonLd(cleanSchema)
            }}
        />
    );
}

/**
 * Компонент для генерации BreadcrumbList для страниц провайдеров
 */
export function ProviderBreadcrumbJsonLd({
    items
}) {
    if (!items || items.length === 0) return null;

    const schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
        }))
    };

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
                __html: serializeJsonLd(schema)
            }}
        />
    );
}
